import { GUIDTypeInfo } from '@ndvr/client-gateway-api';

export const SEARCH_ENTITY_ORDER = ['Household', 'Person', 'Organization', 'Account'] as const;

export type SearchEntityType = (typeof SEARCH_ENTITY_ORDER)[number];
export type SearchTab = 'All' | SearchEntityType;

export interface SearchResultItem {
    kind: 'result';
    id: string;
    type: SearchEntityType;
    typeLabel: string;
    name: string;
    subtitle?: string;
    href: string;
    initials: string;
}

export interface SuggestedSearchItem {
    kind: 'suggested';
    id: string;
    name: string;
    typeLabel: string;
    href: string;
}

export type SearchSelectableItem = SearchResultItem | SuggestedSearchItem;

export interface SearchGroup {
    type: SearchEntityType;
    total: number;
    hasMore: boolean;
    items: SearchResultItem[];
}

export interface SearchIntentSignal {
    intent: 'SEARCH' | 'AI_QUESTION';
    prompt: string;
    strength: 'STRONG' | 'SOFT';
}

export interface SearchMapping {
    groups: SearchGroup[];
    totalResults: number;
    intentSignal: SearchIntentSignal | null;
    aiSuggestionPrompt: string | null;
}

export const EMPTY_SEARCH_GROUPS: SearchGroup[] = SEARCH_ENTITY_ORDER.map((type) => ({
    type,
    total: 0,
    hasMore: false,
    items: [],
}));

export const EMPTY_SEARCH_MAPPING: SearchMapping = {
    groups: EMPTY_SEARCH_GROUPS,
    totalResults: 0,
    intentSignal: null,
    aiSuggestionPrompt: null,
};

export const DEFAULT_SUGGESTED_SEARCHES: SuggestedSearchItem[] = [
    {
        kind: 'suggested',
        id: 'households',
        name: 'Browse households',
        typeLabel: 'Shortcut',
        href: '/household',
    },
    {
        kind: 'suggested',
        id: 'tasks',
        name: 'Open task queue',
        typeLabel: 'Shortcut',
        href: '/tasks',
    },
    {
        kind: 'suggested',
        id: 'reports',
        name: 'View reports',
        typeLabel: 'Shortcut',
        href: '/reports',
    },
];

export const getSearchSelectableItemKey = (item: SearchSelectableItem) => `${item.kind}:${item.id}`;

export const getSearchOptionId = (item: SearchSelectableItem) =>
    item.kind === 'suggested' ? `claro-search-option-suggested-${item.id}` : `claro-search-option-${item.id}`;

export const getActiveSearchItemIndex = ({
    activeItemKey,
    currentIndex,
    items,
}: {
    activeItemKey: string | null;
    currentIndex: number;
    items: SearchSelectableItem[];
}) => {
    if (items.length === 0) {
        return -1;
    }

    if (activeItemKey) {
        const matchingIndex = items.findIndex((item) => getSearchSelectableItemKey(item) === activeItemKey);
        if (matchingIndex >= 0) {
            return matchingIndex;
        }
    }

    if (currentIndex < 0) {
        return 0;
    }

    return Math.min(currentIndex, items.length - 1);
};

export const buildSearchMapping = (results: GUIDTypeInfo[]): SearchMapping => {
    const groups = new Map<SearchEntityType, SearchResultItem[]>(
        SEARCH_ENTITY_ORDER.map((type) => [type, [] as SearchResultItem[]]),
    );
    let intentSignal: SearchIntentSignal | null = null;
    let aiSuggestionPrompt: string | null = null;

    for (const result of results) {
        const nextIntentSignal = extractIntentSignal(result);
        if (nextIntentSignal) {
            intentSignal = nextIntentSignal;
            continue;
        }

        aiSuggestionPrompt = extractAiSuggestionPrompt(result) ?? aiSuggestionPrompt;

        const searchItem = mapSearchResult(result);
        if (searchItem) {
            groups.get(searchItem.type)?.push(searchItem);
        }
    }

    const normalizedGroups = SEARCH_ENTITY_ORDER.map((type) => {
        const items = [...(groups.get(type) ?? [])].sort(compareSearchResults);
        return {
            type,
            total: items.length,
            hasMore: false,
            items,
        };
    });

    return {
        groups: normalizedGroups,
        totalResults: normalizedGroups.reduce((total, group) => total + group.total, 0),
        intentSignal,
        aiSuggestionPrompt,
    };
};

export const getGroupForTab = (groups: SearchGroup[], tab: SearchEntityType): SearchGroup =>
    groups.find((group) => group.type === tab) ?? {
        type: tab,
        total: 0,
        hasMore: false,
        items: [],
    };

export const deriveInitials = (name: string): string => {
    const fallback = name
        .replace(/[^A-Za-z0-9]/g, '')
        .slice(0, 2)
        .toUpperCase();
    const cleanedName = name
        .replace(/,/g, ' ')
        .replace(/\band\b/gi, ' ')
        .replace(/\s*&\s*/g, ' ')
        .trim();
    const tokens = cleanedName.split(/\s+/).filter(Boolean);

    if (tokens.length === 0) {
        return fallback || 'NA';
    }

    if (tokens.length === 1) {
        return tokens[0].slice(0, 2).toUpperCase();
    }

    return `${tokens[0][0] ?? ''}${tokens[tokens.length - 1][0] ?? ''}`.toUpperCase();
};

const extractIntentSignal = (result: GUIDTypeInfo): SearchIntentSignal | null => {
    if (result.type !== 'SearchIntentSignal') {
        return null;
    }

    const intent = getDetailValue(result, 'Intent');
    const prompt = getDetailValue(result, 'Prompt') ?? '';
    const strength = getDetailValue(result, 'Strength');

    if ((intent === 'SEARCH' || intent === 'AI_QUESTION') && prompt) {
        return {
            intent,
            prompt,
            strength: strength === 'SOFT' ? 'SOFT' : 'STRONG',
        };
    }

    return null;
};

const extractAiSuggestionPrompt = (result: GUIDTypeInfo): string | null => {
    if (result.type !== 'AiQuestionSuggestion') {
        return null;
    }

    return getDetailValue(result, 'Prompt') ?? null;
};

const mapSearchResult = (result: GUIDTypeInfo): SearchResultItem | null => {
    switch (result.type) {
        case 'GlobalAccount': {
            const name = result.details[0]?.value ?? result.guid;
            return createSearchResult(result.guid, 'Household', 'Household', name, `/household/${result.guid}`);
        }
        case 'PersonOrg': {
            const subtype = result.details[0]?.value;
            if (subtype === 'JOINT_PERSONS') {
                return null;
            }

            const name = result.details[1]?.value ?? result.guid;
            const subtitle =
                getDetailValue(result, 'Primary E-mail') ??
                getDetailValue(result, 'Email') ??
                getDetailValue(result, 'City');

            if (subtype === 'PERSON') {
                return createSearchResult(result.guid, 'Person', 'Person', name, `/person/${result.guid}`, subtitle);
            }

            return createSearchResult(
                result.guid,
                'Organization',
                subtype === 'TRUST' ? 'Trust' : 'Organization',
                name,
                `/person/${result.guid}`,
                subtitle,
            );
        }
        case 'Client': {
            const name = result.details[0]?.value ?? result.guid;
            const personOrgGUID = result.details[1]?.value;
            return createSearchResult(
                result.guid,
                'Account',
                'Account owner',
                name,
                personOrgGUID ? `/person/${personOrgGUID}` : `/person/${result.guid}`,
            );
        }
        case 'AccountGroup': {
            const name = result.details[1]?.value ?? result.guid;
            return createSearchResult(result.guid, 'Account', 'Portfolio', name, `/account-group/${result.guid}`);
        }
        case 'Account': {
            const name = result.details[2]?.value ?? result.guid;
            return createSearchResult(result.guid, 'Account', 'Account', name, `/account/${result.guid}`);
        }
        default:
            return null;
    }
};

const createSearchResult = (
    id: string,
    type: SearchEntityType,
    typeLabel: string,
    name: string,
    href: string,
    subtitle?: string,
): SearchResultItem => ({
    kind: 'result',
    id,
    type,
    typeLabel,
    name,
    subtitle,
    href,
    initials: deriveInitials(name),
});

const compareSearchResults = (left: SearchResultItem, right: SearchResultItem) => {
    const nameComparison = left.name.localeCompare(right.name, undefined, {
        sensitivity: 'base',
        numeric: true,
    });

    if (nameComparison !== 0) {
        return nameComparison;
    }

    return left.id.localeCompare(right.id, undefined, {
        sensitivity: 'base',
        numeric: true,
    });
};

const getDetailValue = (result: GUIDTypeInfo, label: string) =>
    result.details.find((detail) => detail.label === label)?.value;
